import React from 'react';
import { STAGE_META, navigationMomentum } from '../../lib/journeySignals';

const ORDER = ['capability', 'visibility', 'confidence', 'coordination', 'value', 'navigation'];

const TONE = {
  emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  teal: 'bg-teal-100 text-teal-700 border-teal-200',
  slate: 'bg-slate-100 text-slate-600 border-slate-200',
  amber: 'bg-amber-100 text-amber-700 border-amber-200',
  rose: 'bg-rose-100 text-rose-700 border-rose-200',
};

/**
 * Journey Health Ribbon — the executive summary strip above the trend
 * graph. Aggregates the existing LEI signals into Missing Middle stage
 * scores (no new math) and shows Navigation Momentum (direction).
 */
export function JourneyHealthRibbon({ stageHealth }) {
  if (!stageHealth) return null;
  const momentum = navigationMomentum(stageHealth.navigation?.changePct);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="journey-health-ribbon">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Journey Health</p>
        <span
          className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${TONE[momentum.tone]}`}
          data-testid="navigation-momentum-badge"
          title="Navigation Momentum — the direction of the weighted composite over the loaded window. Navigation is directional: where we are matters less than where we're heading."
        >
          Navigation Momentum: {momentum.label}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {ORDER.map((key) => {
          const meta = STAGE_META[key];
          const s = stageHealth[key];
          const isNav = key === 'navigation';
          return (
            <div
              key={key}
              data-testid={`ribbon-stage-${key}`}
              className={isNav
                ? 'rounded-lg bg-slate-900 px-3 py-2.5'
                : 'rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5'}
              title={isNav
                ? 'Composite Navigation Score — pillar-weighted mean of the five condition stages (= your Navigation Conditions Index / overall LEI)'
                : `Mean of the ${s?.signalCount ?? 0} ${meta.label} signal scores`}
            >
              <p className={`text-[10px] font-semibold uppercase tracking-widest ${isNav ? 'text-cyan-300' : 'text-slate-500'}`}>
                {meta.label}
              </p>
              <div className="flex items-baseline gap-2">
                <p className={`text-2xl font-extrabold tabular-nums leading-tight ${isNav ? 'text-white' : 'text-slate-900'}`}>
                  {s ? `${Math.round(s.latest)}%` : '—'}
                </p>
                {s?.changePct != null && (
                  <span className={`text-xs font-semibold tabular-nums ${
                    s.changePct > 0 ? 'text-emerald-500' : s.changePct < 0 ? 'text-rose-500' : 'text-slate-400'
                  }`}>
                    {s.changePct > 0 ? '+' : ''}{s.changePct.toFixed(1)}%
                  </span>
                )}
              </div>
              <div className={`h-1.5 mt-1.5 rounded-full overflow-hidden ${isNav ? 'bg-slate-700' : 'bg-slate-200/70'}`}>
                <div
                  className="h-full rounded-full"
                  style={{ width: `${Math.min(100, Math.max(0, s?.latest ?? 0))}%`, backgroundColor: meta.color }}
                />
              </div>
              <p className={`text-[10px] mt-1 ${isNav ? 'text-slate-400' : 'text-slate-400'}`}>
                {isNav ? 'weighted composite' : `${s?.signalCount ?? 0} ${s?.signalCount === 1 ? 'signal' : 'signals'}`}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
